/** Small formatters for the sheet: dates in the list's one format ("12 Mar 2026") and the words for ledger events. */
import type { LifecycleEventType } from "../../api/lifecycleEvents.types";
import type { PersonIdentity } from "../../api/person360.types";
import { fmtDate as listDate } from "../data/format";

/** "2026-03-12" (or a full ISO stamp) as "12 Mar 2026"; "" for null. The calendar day, never shifted by the zone. */
export function fmtDate(iso: string | null | undefined): string {
  const out = listDate(iso?.slice(0, 10));
  return out === "—" ? "" : out;
}

/** The ledger's event types: the API's own list, and the two billing events the ledger also records (#440), which the shared type does not name. */
export type LedgerEvent = LifecycleEventType | "payment_issue" | "payment_recovered";

export const EVENT_LABEL: Record<LedgerEvent, string> = {
  joined: "Joined",
  lapsed: "Lapsed",
  reinstated: "Rejoined",
  dropped: "Dropped",
  bucket_changed: "Moved",
  baseline: "Baseline",
  payment_issue: "Payment issue",
  payment_recovered: "Payment recovered",
};

/**
 * A quiet note under Keystone's licence date (#635), only when it says something: nothing when the state list and
 * CE21 agree; which one it came from, and what the other holds, when they differ; which one alone has a date.
 * Nothing from a backend that does not send the state's date.
 */
export function sourceNote(identity: Pick<PersonIdentity, "license_expires_on" | "license_state_expires_on">): string {
  const { license_state_expires_on: state, license_expires_on: ce21 } = identity;
  if (state === undefined) return "";
  if (state && ce21) return state === ce21 ? "" : state > ce21 ? `From the state list; CE21 has ${fmtDate(ce21)}` : `From CE21; the state list has ${fmtDate(state)}`;
  if (ce21) return "CE21 only";
  if (state) return "State list only";
  return "";
}

/** A moment as the local calendar day, YYYY-MM-DD. */
export const localDay = (iso: string): string => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** What the sheet's header says of a person added in Keystone: "Added from the state list on 29 Oct 2026" (#635), else "Added in Keystone". */
export function addedWords(added: { at: string; from?: string | null }): string {
  return added.from === "state_list" ? `Added from the state list on ${fmtDate(localDay(added.at))}` : "Added in Keystone";
}

/** An address as the panel compares it, whichever source gave it. */
export interface Place {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
}

/** Compared loosely: case, punctuation and spacing aside. */
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/** US states and DC by name, so "MI" (the state list) and "Michigan" (CE21) compare equal. */
const USPS: Readonly<Record<string, string>> = Object.fromEntries(
  "al alabama|ak alaska|az arizona|ar arkansas|ca california|co colorado|ct connecticut|de delaware|dc district of columbia|fl florida|ga georgia|hi hawaii|id idaho|il illinois|in indiana|ia iowa|ks kansas|ky kentucky|la louisiana|me maine|md maryland|ma massachusetts|mi michigan|mn minnesota|ms mississippi|mo missouri|mt montana|ne nebraska|nv nevada|nh new hampshire|nj new jersey|nm new mexico|ny new york|nc north carolina|nd north dakota|oh ohio|ok oklahoma|or oregon|pa pennsylvania|ri rhode island|sc south carolina|sd south dakota|tn tennessee|tx texas|ut utah|vt vermont|va virginia|wa washington|wv west virginia|wi wisconsin|wy wyoming"
    .split("|")
    .map((pair) => [pair.slice(3), pair.slice(0, 2)]),
);
const stateOf = (s: string) => USPS[norm(s)] ?? norm(s);

/** A street with its second line, compared loosely: "Ste. 210", "Suite #210" and "# 210" are one unit. */
const street = (p: Place) =>
  norm(
    `${p.line1} ${p.line2}`
      .toLowerCase()
      .replace(/(?:\b(?:suite|ste|apartment|apt|unit|room|rm)\b\.?\s*#?|#)\s*([a-z0-9]+)/g, " unit $1 "),
  );

/**
 * Whether two sources give the same address: the same street and second line (a suite on one side only is another
 * address: correct beats compact), in the same state, and the same ZIP, or the same town when either has no ZIP.
 * Not the same when neither ZIP nor town can be compared.
 */
export function sameAddress(a: Place, b: Place): boolean {
  const [sa, sb] = [street(a), street(b)];
  if (!sa || !norm(a.line1 || a.line2) || sa !== sb) return false;
  if (a.state && b.state && stateOf(a.state) !== stateOf(b.state)) return false;
  const zip = (z: string) => z.trim().slice(0, 5);
  if (zip(a.zip) && zip(b.zip)) return zip(a.zip) === zip(b.zip);
  return !!norm(a.city) && norm(a.city) === norm(b.city);
}
